import { Component, OnInit, inject } from '@angular/core';
import {EMPTY, Observable, Subject} from "rxjs";
import {
    AddressFinderService,
    AddressUpdateService,
    ProblemInstanceService,
    TaskService,
    TaskView
} from "planner-shared";
import {FormBuilder, FormGroup} from "@angular/forms";
import {Address} from "../../../shared/models/address.model";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import {debounceTime, filter, map, switchMap} from "rxjs/operators";
import {EmployeeAdminTaskAddressUpdate} from "../../../shared/models/employee-admin-task-address-update.model";
import {ModalService, ModalType, NotificationService} from "@shared";
import {EmployeeAdminTaskAddress} from "../../../shared/models/employee-admin-task-address.model";
import {TranslateService} from "@ngx-translate/core";

@UntilDestroy()
@Component({
    selector: 'app-admin-task-modal',
    templateUrl: './admin-task-modal.component.html',
    styleUrls: ['./admin-task-modal.component.scss'],
    standalone: false
})
export class AdminTaskModalComponent implements OnInit {
    private readonly formBuilder = inject(FormBuilder);
    private readonly problemInstanceService = inject(ProblemInstanceService);
    private readonly addressFinderService = inject(AddressFinderService);
    private readonly addressUpdateService = inject(AddressUpdateService);
    private readonly notificationService = inject(NotificationService);
    private readonly taskService = inject(TaskService);
    private readonly modalService = inject(ModalService);
    private readonly translateService = inject(TranslateService);


    public set parameter(administrativeTask: TaskView) {
        this.administrativeTask = administrativeTask;
        this.initAddressForm();
    }

    public cancel$: Subject<void>;

    public administrativeTask: TaskView;

    public editing = false;
    public addressForm: FormGroup;
    public validating: boolean;
    public addressSuggestions: Address[];
    public readonly VALIDATE_DELAY_TIME = 800;
    private readonly validate$: Subject<string>;
    public addressNotValid: boolean;
    public submitEnabled: boolean;

    constructor() {
        this.addressForm = this.formBuilder.group({
            address: [null],
            status: [null],
            zipCode: [null]
        });
        this.cancel$ = new Subject();
        this.addressNotValid = true;
        this.validate$ = new Subject<string>();
        this.submitEnabled = true;
    }

    ngOnInit(): void {
        this.validate$
            .pipe(
                untilDestroyed(this),
                debounceTime(this.VALIDATE_DELAY_TIME),
                switchMap(() => this.checkAddress())
            )
            .subscribe();
    }

    public dismissModal(): void {
        this.cancel$.next();
    }

    public editClick(): void {
        this.editing = true;
    }

    public validateAddress(): void {
        this.validating = true;
        this.addressNotValid = false;
        this.validate$.next(null);
    }
    public updateAddressClick(){
        const message: string = this.translateService.instant('ADDRESS_ADMIN_TASK_UPDATE_WARNING', {
            taskDescription: this.administrativeTask.serviceName
        });
        this.modalService.openInformationModal(
            ModalType.Warning,
            message,
            () => this.updateAddress(),
            null,
        );
    }
    public updateAddress(): void {

        const updateAddress: EmployeeAdminTaskAddressUpdate = new EmployeeAdminTaskAddressUpdate(
            this.administrativeTask.originalTaskId,
            this.addressForm.value.address.streetNameAndHouseNumber,
            this.addressForm.value.zipCode
        );
        this.submitEnabled = false;
        this.addressUpdateService.updateAddressForAdministrativeTask(updateAddress).subscribe(
            () => {
                this.dismissModal();
                this.submitEnabled = true;
                this.notificationService.success('ADDRESS_ADMIN_TASK_UPDATED');
                this.administrativeTask.zipCode = updateAddress.zipCode;
                this.administrativeTask.streetAddressNameNumberAndLetter = updateAddress.streetAddressNameNumberAndLetter;
                this.problemInstanceService.getProblemInstance().subscribe(problemInstance => {
                    problemInstance.problemInstance.geTasksNotPatients
                        .filter(task => task.originalTaskId === this.administrativeTask.originalTaskId)
                        .forEach(task => {
                            task.streetAddressNameNumberAndLetter = updateAddress.streetAddressNameNumberAndLetter;
                            task.zipCode = updateAddress.zipCode;
                        });
                    problemInstance.problemInstance.emTasksNotPatients
                        .filter(task => task.originalTaskId === this.administrativeTask.originalTaskId)
                        .forEach(task => {
                            task.streetAddressNameNumberAndLetter = updateAddress.streetAddressNameNumberAndLetter;
                            task.zipCode = updateAddress.zipCode;
                        });
                });
                this.taskService.reloadGeneralTasks();
                this.addressUpdateService.updateTasksSubject$.next();
            },
            () => {
                this.dismissModal();
            }
        );
    }

    private checkAddress(): Observable<void> {
        if (!this.validating) {
            return EMPTY;
        }
        const adminTaskAddressRequest = new EmployeeAdminTaskAddress(
            this.administrativeTask.originalTaskId,
            this.addressForm.value.address.streetNameAndHouseNumber,
            this.addressForm.value.address.zipCode
        );
        return this.problemInstanceService.getProblemInstance().pipe(
            switchMap(result => {
                return this.addressFinderService.validateAdminTaskAddress(adminTaskAddressRequest, result.problemInstance.officeInfo);
            }),
            filter(result => result.patientToAddressStatusList && result.patientToAddressStatusList.length > 0),
            map(result => {
                const patientAddressStatus = result.patientToAddressStatusList[0];
                this.addressForm.controls.status.setValue(patientAddressStatus.status);
                this.addressForm.controls.address.setValue(
                    <Address>{
                        streetNameAndHouseNumber: adminTaskAddressRequest.streetAddressNameNumberAndLetter,
                        zipCode: patientAddressStatus.addressSuggestions[0].zipCode,
                        userSelectedOption: false
                    });
                this.addressForm.controls.zipCode.setValue(patientAddressStatus.addressSuggestions[0].zipCode);
                this.addressSuggestions = patientAddressStatus.addressSuggestions;
                this.validating = false;
                this.addressNotValid = patientAddressStatus.status;
            })
        );
    }

    private initAddressForm() {
        this.addressForm.reset();
        this.addressForm.controls.address.enable();
        this.addressForm.controls.address.setValue(<Address>{
            streetNameAndHouseNumber:
                this.administrativeTask.streetAddressNameNumberAndLetter ? this.administrativeTask.streetAddressNameNumberAndLetter.trim():'',
            zipCode: this.administrativeTask.zipCode
        });
        this.addressForm.controls.zipCode.setValue(this.administrativeTask.zipCode);
        this.addressForm.markAsPristine();
    }

}
